import { Fragment, useCallback, useEffect, useRef, useState, type JSX } from "react";
import { Paperclip } from "react-feather";

import ApiHttp from "byzantine/src/ApiHttp";
import Filters from "byzantine/src/filters";
import ThreadReply from "./ThreadReply";

type DateBoundaryProps = {
  date: string | null;
};

type FileUrlLink = {
  text: string;
  link: string;
};

type MessageItemProps = {
  isSelf: boolean;
  messageBody: string;
  messageDateTime: string;
};

type ThreadAuthor = {
  id: string;
};

type ThreadMessage = {
  id: string;
  body: string;
  created_at: string;
  author: ThreadAuthor;
};

type ThreadData = {
  subject: string;
  is_read: boolean;
};

type ThreadResponse = {
  thread: ThreadData;
};

type ThreadMessagesResponse = {
  messages: ThreadMessage[];
};

type ThreadMessageListProps = {
  threadId?: string | null;
  currentUserUUID?: string;
  replyDisclaimer?: string;
  replyTime?: string;
  userMessageUploads?: boolean;
};

function DateBoundary({ date }: DateBoundaryProps) {
  if (date === null) {
    return null;
  }

  return (
    <div className="date-boundary">
      <div className="date">{Filters.longMonthDayYear(date)}</div>
    </div>
  );
}

function linebreaks(str: string): JSX.Element {
  const strTrimmed = str.trim();
  const paragraphEndIndex = strTrimmed.indexOf("\n\n");
  const parts =
    paragraphEndIndex !== -1
      ? strTrimmed.substring(0, paragraphEndIndex).split("\n")
      : strTrimmed.split("\n");

  return (
    <>
      <p>
        {parts.map((part, index) => (
          <Fragment key={index}>
            {part}
            <br />
          </Fragment>
        ))}
      </p>
      {paragraphEndIndex !== -1
        ? linebreaks(strTrimmed.substring(paragraphEndIndex))
        : null}
    </>
  );
}

export function parseFileUrlLink(str: string): FileUrlLink | null {
  // Attempt to split the markdown [text](link) format
  const match = str.match(/^\[(.*?)\]\((.*)\)$/);
  if (!match) {
    return null;
  }

  const text = match[1];
  const urlString = match[2];

  try {
    const parsedUrl = new URL(urlString);
    if (parsedUrl.protocol !== "https:") {
      return null;
    }

    return {
      text,
      link: parsedUrl.href,
    };
  } catch {
    // URL is invalid
    return null;
  }
}

function MessageItem({
  isSelf,
  messageBody,
  messageDateTime,
}: MessageItemProps) {
  const messageItemClasses = isSelf ? "message-item self" : "message-item";
  const fileMeta = parseFileUrlLink(messageBody);
  const formattedMessageBody =
    fileMeta !== null ? (
      <div className="file-message">
        <Paperclip size={16} />
        <a href={fileMeta.link}>{fileMeta.text}</a>
      </div>
    ) : (
      linebreaks(messageBody)
    );

  return (
    <div className={messageItemClasses}>
      <div className="message-author">{isSelf ? "Self" : "Support"}</div>
      <div className="message-body">{formattedMessageBody}</div>
      <div className="message-datetime">{Filters.time(messageDateTime)}</div>
    </div>
  );
}

export default function ThreadMessageList({
  threadId,
  currentUserUUID,
  replyDisclaimer,
  replyTime,
  userMessageUploads,
}: ThreadMessageListProps) {
  const messageList = useRef<HTMLDivElement | null>(null);
  const [thread, setThread] = useState<ThreadData | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[] | null>(null);

  const reloadThread = useCallback(() => {
    if (!threadId) {
      return;
    }

    ApiHttp.fetch(`threads/${threadId}`).then((result: ThreadResponse) => {
      setThread(result.thread);
      if (result.thread.is_read) {
        return;
      }

      ApiHttp.fetch(`threads/${threadId}/mark_read`, { method: "PUT" });
    });

    ApiHttp.fetch(`threads/${threadId}/messages`).then(
      (result: ThreadMessagesResponse) => {
        setMessages(result.messages);
        if (messageList.current) {
          messageList.current.scrollTop = 0;
        }
      }
    );
  }, [threadId]);

  useEffect(() => {
    reloadThread();
  }, [reloadThread]);

  if (!thread || !messages || !threadId) {
    return <div className="thread-message-list"></div>;
  }

  let currentDate: string | null = null;

  return (
    <div className="thread-message-list">
      <div className="thread-subject">{thread.subject}</div>
      <div className="message-list-scroller" ref={messageList}>
        <div className="message-list">
          {messages.map((message, index) => {
            const nextDate = message.created_at;
            const isLastMessage = messages.length - 1 === index;
            const didDateChange =
              currentDate !== null &&
              Filters.americanDate(currentDate) !==
                Filters.americanDate(nextDate);
            const fragment = (
              <Fragment key={message.id}>
                {didDateChange ? <DateBoundary date={currentDate} /> : null}
                <MessageItem
                  isSelf={message.author.id === currentUserUUID}
                  messageBody={message.body}
                  messageDateTime={message.created_at}
                />
                {isLastMessage ? <DateBoundary date={nextDate} /> : null}
              </Fragment>
            );

            currentDate = nextDate;
            return fragment;
          })}
        </div>
      </div>
      <ThreadReply
        threadId={threadId}
        reloadThread={reloadThread}
        replyTime={replyTime}
        replyDisclaimer={replyDisclaimer}
        userMessageUploads={userMessageUploads}
      />
    </div>
  );
}
